/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { Announced, IStackProps, IStackSlots, Separator, Stack } from '@fluentui/react';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';

export type DistributionTypes = 'intents' | 'entitiesInTags' | 'entitiesInUtterances' | 'linkedIntents' | 'nonLinkedIntents';
type DistributionBarProps = {
    text: string;
    testingColor: string;
    testingCount: number;
    trainingColor: string;
    trainingCount: number;
    testingPercent: number;
    trainingPercent: number;
    displaySeparator: boolean;
};

const messages = defineMessages({
    datasetPercentAriaLabel: {
        id: 'dataDistribution.distributionBar.datasetPercentAriaLabel',
        defaultMessage: '{sectionName} section, {datasetType} dataset, {percentage}%'
    }
});

const barContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { width: '100%', border: '1px solid' }
});
const barStyles = (backgroundColor: string, width: number) =>
    classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { height: '12px', width: `${width}%`, backgroundColor } });

export const DistributionBar = (props: DistributionBarProps) => {
    const { text, testingColor, testingCount, trainingColor, trainingCount, displaySeparator, testingPercent, trainingPercent } = props;
    const { localize } = useLocalization();

    return (
        <Stack>
            <Stack tokens={{ childrenGap: 10 }}>
                <TruncatedText title={text} maxWidth="100%">
                    {text}
                </TruncatedText>

                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 3 }}>
                    <Stack styles={barContainerStyles}>
                        <Stack styles={barStyles(trainingColor, trainingPercent || 1)} />
                    </Stack>

                    <Announced
                        message={localize(messages.datasetPercentAriaLabel, {
                            sectionName: text,
                            datasetType: 'training',
                            percentage: trainingPercent.toFixed()
                        })}
                    />

                    <LuisText aria-hidden>{trainingCount}</LuisText>
                </Stack>

                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 3 }}>
                    <Stack styles={barContainerStyles}>
                        <Stack styles={barStyles(testingColor, testingPercent || 1)} />
                    </Stack>

                    <Announced
                        message={localize(messages.datasetPercentAriaLabel, {
                            sectionName: text,
                            datasetType: 'testing',
                            percentage: testingPercent.toFixed()
                        })}
                    />

                    <LuisText aria-hidden>{testingCount}</LuisText>
                </Stack>
            </Stack>

            {displaySeparator && <Separator />}
        </Stack>
    );
};
